import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';
import cn from 'classnames';
import Button from 'react-bootstrap/lib/Button';
import Dropdown from 'react-bootstrap/lib/Dropdown';
import MenuItem from 'react-bootstrap/lib/MenuItem';
import FormGroup from 'react-bootstrap/lib/FormGroup';
import ControlLabel from 'react-bootstrap/lib/ControlLabel';
import Checkbox from 'react-bootstrap/lib/Checkbox';
import moment from 'moment';

import config from '../../config';
import DatePicker from '../../widgets/DatePicker';
import { toCityTimezone } from '../../utils/dates';

import 'react-day-picker/lib/style.css';
import './search-form.css';

const validationScheme = {
  country: ({ country }) => !country,
  pickUpCity: ({ pickUpCity }) => !pickUpCity,
  dropOfCity: ({ dropOfCity, returnAtSameCity }) => !returnAtSameCity && !dropOfCity,
  bikeTypes: ({ bikeTypes }) => !bikeTypes.length
};

const validateField = (name, data) => {
  const errors = { [name]: null };
  const scheme = validationScheme[name];

  if (scheme && scheme(data)) {
    errors[name] = 'error';
  }

  return errors;
};

const validate = (data) => {
  let errors = {};
  const names = Object.keys(validationScheme);

  names.forEach((name) => {
    errors = {
      ...errors,
      ...validateField(name, data)
    };
  });

  return {
    errors,
    isValid: !Object.values(errors).filter(Boolean).length
  };
};

class SearchForm extends Component {
  constructor(...args) {
    super(...args);

    this.toggleBeginDayPicker = this.toggleDayPicker.bind(this, 'begin');
    this.toggleEndDayPicker = this.toggleDayPicker.bind(this, 'end');

    this.beginDayChangeHandler = this.onDaySelect.bind(this, 'begin');
    this.endDayChangeHandler = this.onDaySelect.bind(this, 'end');

    this.checkboxChangeHandler = this.onCheckboxChange.bind(this);
    this.submitHandler = this.onSubmit.bind(this);

    this.onSelectBeginDateTime = this.onSelect.bind(this, 'beginDateTime');
    this.onSelectEndDateTime = this.onSelect.bind(this, 'endDateTime');

    const todayMorningTenHours = moment().startOf('day').set('hour', 10);
    const beginDateTime = moment(todayMorningTenHours).add(2, 'day').toISOString();
    const endDateTime = moment(todayMorningTenHours).add(3, 'day').toISOString();

    this.state = {
      countriesOptions: [],
      bikeTypesOptions: [],
      country: null,
      pickUpCity: null,
      dropOfCity: null,
      beginDateTime,
      endDateTime,
      bikeTypes: [],

      returnAtSameCity: true,
      beginDayPickerOpen: false,
      endDayPickerOpen: false,

      errors: {}
    };
  }

  componentWillMount() {
    this.generateCountriesOptions(this.props.countries);
    this.generateBikeTypesOptions(this.props.bikeTypes);

    if (this.props.initialState) {
      this.setState(this.props.initialState);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.countries !== this.props.countries) {
      this.generateCountriesOptions(nextProps.countries);
    }

    if (nextProps.bikeTypes !== this.props.bikeTypes) {
      this.generateBikeTypesOptions(nextProps.bikeTypes);
    }

    if (nextProps.initialState !== this.props.initialState) {
      if (nextProps.initialState) {
        this.setState(nextProps.initialState);
      }
    }
  }

  generateCountriesOptions(countries) {
    if (!countries) {
      this.setState({
        countriesOptions: [],
        country: null,
        pickUpCity: null,
        dropOfCity: null
      });

      return;
    }

    const countriesOptions = countries.map(country => {
      const cities = country.cities.map(city => ({
        title: city.name,
        value: city.id,
        timezoneName: city.timezoneName
      }));

      const dropOffLocations = country.dropOffLocations.map(city => ({
        title: city.name,
        value: city.id,
        timezoneName: city.timezoneName
      }));

      return {
        title: country.name,
        value: country.id,
        cities,
        dropOffLocations
      };
    });

    this.setState({ countriesOptions });
  }

  generateBikeTypesOptions(bikeTypes) {
    if (!bikeTypes) {
      this.setState({
        bikeTypesOptions: [],
        bikeTypes: []
      });

      return;
    }

    const bikeTypesOptions = bikeTypes.map(bikeType => ({
      title: bikeType.name,
      value: bikeType.id
    }));

    this.setState({
      bikeTypesOptions,
      bikeTypes: bikeTypes.map(bt => bt.id)
    });
  }

  toggleDayPicker(kind) {
    this.setState((prevState) => ({
      beginDayPickerOpen: kind === 'begin' ? !prevState.beginDayPickerOpen : false,
      endDayPickerOpen: kind === 'end' ? !prevState.endDayPickerOpen : false
    }));
  }

  onCheckboxChange({ target }) {
    this.setState((prevState) => {
      const update = {
        [target.name]: !prevState[target.name]
      };

      if (target.name === 'returnAtSameCity') {
        update.dropOfCity = prevState.pickUpCity;
      }

      return update;
    });
  }

  updateDateTime(kind, newMoment) {
    const paramName = `${kind}DateTime`;

    const minBegin = moment()
      .set('minutes', 0)
      .set('seconds', 0)
      .set('milliseconds', 0)
      .add(1, 'hour')
      .add(config.minimalBookingOffset, 'hour');

    if (kind === 'begin' && newMoment.isBefore(minBegin)) {
      newMoment = minBegin;
    }

    const newState = { [paramName]: newMoment.toISOString() };

    if (kind === 'begin') {
      const endMoment = moment(new Date(this.state.endDateTime));

      if (newMoment.isAfter(endMoment.subtract(1, 'hour'))) {
        newState.endDateTime = moment(newMoment).add(1, 'day').toISOString();
      }
    } else if (kind === 'end') {
      const beginMoment = moment(new Date(this.state.beginDateTime));

      if (newMoment.isBefore(beginMoment.add(1, 'hour'))) {
        newState.endDateTime = moment(beginMoment).toISOString();
      }
    }

    this.setState(newState);
  }

  onSelect = (key, value) => {
    let validateFields = [];
    let update = { [key]: value };

    if (key === 'country') {
      const selectedCountry = this.state.countriesOptions.find(el => el.value === value);
      update.pickUpCity = selectedCountry.cities[0].value;
      update.dropOfCity = selectedCountry.cities[0].value;
      validateFields = ['country', 'pickUpCity', 'dropOfCity'];
    }

    if (key === 'pickUpCity') {
      validateFields = [key];

      if (this.state.returnAtSameCity) {
        update.dropOfCity = value;
        validateFields = ['pickUpCity', 'dropOfCity'];
      }
    }

    if (key === 'beginDateTime') {
      const newDate = moment(new Date(value));
      this.updateDateTime('begin', newDate);
      return;
    }

    if (key === 'endDateTime') {
      const newDate = moment(new Date(value));
      this.updateDateTime('end', newDate);
      return;
    }

    this.setState(update, () => {
      this.validateFieldAndUpdate(this.state, validateFields);
    });
  };

  onDaySelect(kind, date, modifiers) {
    if (modifiers.disabled) {
      return;
    }

    const country = this.state.countriesOptions.find(el => el.value === this.state.country);

    const cityId = this.state[`${kind === 'begin' ? 'pickUp' : 'dropOf'}City`];
    const city = country && country.cities.find(el => el.value === cityId);

    const prevMoment = moment(new Date(this.state[`${kind}DateTime`]));
    if (city && city.timezoneName) {
      prevMoment.tz(city.timezoneName); // keepTime = false
    }

    const newMoment = moment(new Date(date));
    if (city && city.timezoneName) {
      newMoment.tz(city.timezoneName, true); // keepTime = true
    }

    newMoment
      .set('hour', prevMoment.get('hour'))
      .set('minute', prevMoment.get('minute'));

    this.toggleDayPicker();
    this.updateDateTime(kind, newMoment);
  }

  onToggleBikeType(bikeType) {
    this.setState((prevState) => {
      const bikeTypes = [...prevState.bikeTypes];
      const index = bikeTypes.indexOf(bikeType);

      if (index > -1) {
        bikeTypes.splice(index, 1);
      } else {
        bikeTypes.push(bikeType);
      }

      return { bikeTypes };
    }, () => {
      this.validateFieldAndUpdate(this.state, ['bikeTypes']);
    });
  }

  validateFieldAndUpdate(data, names) {
    this.setState(({ errors }) => ({
      errors: {
        ...errors,
        ...names.reduce((prev, name) => ({
          ...prev,
          ...validateField(name, data)
        }), {})
      }
    }));
  }

  onSubmit(event) {
    event.preventDefault();

    const { errors, isValid } = validate(this.state);
    this.setState({ errors });

    if (isValid) {
      this.props.onSubmit(this.state);
    }
  }

  renderTimezone(city) {
    if (!city || !city.timezoneName) {
      return '';
    }

    const shiftedMoment = toCityTimezone(new Date(), city);
    if (typeof shiftedMoment === 'number') {
      return '';
    }

    const dropOffTz = shiftedMoment.utcOffset() / 60;
    if (dropOffTz >= 0) {
      return `(UTC+${dropOffTz})`;
    } else {
      return `(UTC${dropOffTz})`;
    }
  }

  getDisabledDays() {
    const minBegin = moment()
      .set('minutes', 0)
      .set('seconds', 0)
      .set('milliseconds', 0)
      .add(1, 'hour')
      .add(config.minimalBookingOffset, 'hour');

    return {
      before: minBegin.toDate()
    };
  }

  render() {
    const { t, className } = this.props;
    const { errors } = this.state;
    const countriesOptions = [...this.state.countriesOptions].sort((prev, next) => {
      const prevTitle = t(prev.title, { ns: 'countries' });
      const nextTitle = t(next.title, { ns: 'countries' });
      return prevTitle < nextTitle ? -1 : 1;
    });

    countriesOptions.forEach(country => {
      country.cities.sort((a, b) =>
        t(a.title, { ns: 'cities' }) < t(b.title, { ns: 'cities' }) ? -1 : 1
      );

      country.dropOffLocations.sort((a, b) =>
        t(a.title, { ns: 'cities' }) < t(b.title, { ns: 'cities' }) ? -1 : 1
      );
    });

    const selectedCountry = this.state.countriesOptions.find(el => el.value === this.state.country);
    const selectedPickUpCityCity = selectedCountry &&
      selectedCountry.cities.find(el => el.value === this.state.pickUpCity);
    const selectedDropOfCityCity = selectedCountry &&
      selectedCountry.dropOffLocations.find(el => el.value === this.state.dropOfCity);

    let pickUpTz = '';
    let dropOfTz = '';
    if (selectedPickUpCityCity && selectedDropOfCityCity &&
        selectedDropOfCityCity.timezoneName !== selectedPickUpCityCity.timezoneName) {

      pickUpTz = this.renderTimezone(selectedPickUpCityCity);
      dropOfTz = this.renderTimezone(selectedDropOfCityCity);
    }

    let countryTitle = t('Select country');
    if (selectedCountry) {
      countryTitle = t(selectedCountry.title, { ns: 'countries' });
    }

    let pickUpCityTitle = t('Select city');
    if (selectedPickUpCityCity) {
      pickUpCityTitle = t(selectedPickUpCityCity.title, { ns: 'cities' });
    }

    let dropOfCityTitle = t('Select city');
    if (selectedDropOfCityCity) {
      dropOfCityTitle = t(selectedDropOfCityCity.title, { ns: 'cities' });
    }

    return (
      <div className={cn('search-form-container', { [className]: className })}>
        {(className !== 'horizontal') && (
          <div className="search-form-title">
            {t('Search for rental')}
          </div>
        )}

        <form
          className="search-form clearfix"
          onSubmit={this.submitHandler}
        >
          <FormGroup
            className="select-form-group"
            validationState={errors.country}
          >
            <ControlLabel>
              {t('Country')}
            </ControlLabel>

            <Dropdown id="searchCountrySelect">
              <Dropdown.Toggle noCaret>
                {countryTitle}
                <span className="dropdown-toggle-icon">
                  <span className="caret" />
                </span>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                {countriesOptions.map(el => (
                  <MenuItem
                    key={el.value}
                    eventKey={el.value}
                    active={this.state.country === el.value}
                    onClick={this.onSelect.bind(this, 'country', el.value)}
                  >
                    {t(el.title, { ns: 'countries' })}
                  </MenuItem>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </FormGroup>

          <FormGroup
            className="select-form-group"
            validationState={errors.pickUpCity}
          >
            <ControlLabel>
              {t('Pick-up City')}
            </ControlLabel>

            <Dropdown id="searchCountrySelect">
              <Dropdown.Toggle noCaret>
                {pickUpCityTitle}
                <span className="dropdown-toggle-icon">
                  <span className="caret" />
                </span>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                {!!selectedCountry && selectedCountry.cities.map(el => (
                  <MenuItem
                    key={el.value}
                    eventKey={el.value}
                    active={this.state.pickUpCity === el.value}
                    onClick={this.onSelect.bind(this, 'pickUpCity', el.value)}
                  >
                    {t(el.title, { ns: 'cities' })}
                  </MenuItem>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </FormGroup>

          {!this.state.returnAtSameCity && (
            <FormGroup
              className="select-form-group select-form-group-toggle"
              validationState={errors.dropOfCity}
            >
              <ControlLabel>
                {t('Drop-off City')}
              </ControlLabel>

              <Dropdown id="searchCountrySelect">
                <Dropdown.Toggle noCaret>
                  {dropOfCityTitle}
                  <span className="dropdown-toggle-icon">
                    <span className="caret" />
                  </span>
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  {!!selectedCountry && selectedCountry.dropOffLocations.map(el => (
                    <MenuItem
                      key={el.value}
                      eventKey={el.value}
                      active={this.state.dropOfCity === el.value}
                      onClick={this.onSelect.bind(this, 'dropOfCity', el.value)}
                    >
                      {t(el.title, { ns: 'cities' })}
                    </MenuItem>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </FormGroup>
          )}

          <FormGroup className="checkbox-form-group">
            <Checkbox
              className="styled-checkbox"
              name="returnAtSameCity"
              checked={this.state.returnAtSameCity}
              onChange={this.checkboxChangeHandler}
            >
              <span className="checkbox-icon" />
              {t('Return bike to the same location')}
            </Checkbox>
          </FormGroup>

          <DatePicker
            dayPickerOpened={this.state.beginDayPickerOpen}
            toggleDayPicker={this.toggleBeginDayPicker}
            label={`${t('Pick-up Date')} ${pickUpTz}`}
            timeChangeHandler={this.onSelectBeginDateTime}
            dayChangeHandler={this.beginDayChangeHandler}
            dateTimeValue={this.state.beginDateTime}
            timezone={selectedPickUpCityCity && selectedPickUpCityCity.timezoneName}
            disabledDays={this.getDisabledDays()}
          />

          <DatePicker
            dayPickerOpened={this.state.endDayPickerOpen}
            toggleDayPicker={this.toggleEndDayPicker}
            label={`${t('Drop-off Date')} ${dropOfTz}`}
            timeChangeHandler={this.onSelectEndDateTime}
            dayChangeHandler={this.endDayChangeHandler}
            dateTimeValue={this.state.endDateTime}
            timezone={selectedDropOfCityCity && selectedDropOfCityCity.timezoneName}
            disabledDays={{ before: moment(this.state.beginDateTime).add(1, 'hour').toDate() }}
          />

          <FormGroup
            className="checkboxes-form-group"
            validationState={errors.bikeTypes}
          >
            <ControlLabel>
              {t('Type')}
            </ControlLabel>

            <div className="checkboxes-container">
              {this.state.bikeTypesOptions.map(el => (
                <Checkbox
                  inline
                  className="styled-checkbox"
                  key={el.value}
                  checked={this.state.bikeTypes.indexOf(el.value) > -1}
                  onChange={this.onToggleBikeType.bind(this, el.value)}
                >
                  <span className="checkbox-icon" />
                  {t(el.title, { ns: 'bikeTypes' })}
                </Checkbox>
              ))}
            </div>
          </FormGroup>

          <div className="submit-block">
            {(className !== 'horizontal') && (
              <ul className="advantages-list">
                <li>{t('No hidden commissions')}</li>
                <li>{t('No credit card fees')}</li>
                <li>{t('Search, Compare & Save')}</li>
              </ul>
            )}

            <Button bsStyle="success" type="submit">
              {className === 'horizontal' ? t('New search') : t('Search')}
            </Button>
          </div>
        </form>
      </div>
    );
  }
}

SearchForm.propTypes = {
  className: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  countries: PropTypes.array,
  bikeTypes: PropTypes.array
};

export default translate(['translation', 'countries', 'bikeTypes'])(SearchForm);



// WEBPACK FOOTER //
// ./src/widgets/SearchForm/index.js